import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, PackageOpen } from "lucide-react";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { PageHeader, EmptyState, Loading, Pager } from "../../components/common";
import { ShareDealButton } from "../../components/ShareDealButton";
import { api, mediaUrl } from "../../lib/api";
import { pkr, commissionText, AVAILABILITY } from "../../lib/format";

export const DealCard = ({ d }) => (
  <div className="group flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md" data-testid={`deal-card-${d.ref}`}>
    <Link to={`/app/deals/${d.id}`} className="relative aspect-[16/10] bg-slate-100">
      {d.main_image ? <img src={mediaUrl(d.main_image)} alt={d.name} className="h-full w-full object-cover" loading="lazy" /> : <div className="grid h-full place-items-center text-slate-400"><PackageOpen className="h-10 w-10" /></div>}
      <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 text-xs font-semibold text-slate-700">{d.category}</span>
      {d.availability !== "in_stock" && <span className="absolute right-3 top-3 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-800">{AVAILABILITY[d.availability]}</span>}
    </Link>
    <div className="flex flex-1 flex-col p-5">
      <p className="font-mono text-[11px] text-slate-400">{d.ref}</p>
      <h3 className="mt-1 font-heading text-lg font-semibold leading-snug text-slate-900">{d.name}</h3>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <div className="rounded-lg bg-slate-50 p-3"><p className="text-[11px] uppercase tracking-wider text-slate-500">Customer price</p><p className="font-mono font-bold text-slate-900">{pkr(d.price)}</p></div>
        <div className="rounded-lg bg-emerald-50 p-3"><p className="text-[11px] uppercase tracking-wider text-emerald-700">You earn</p><p className="font-mono font-bold text-emerald-700">{commissionText(d)}</p></div>
      </div>
      <div className="mt-5 flex gap-2 pt-auto">
        <ShareDealButton d={d} className="h-11 w-12 shrink-0 px-0" />
        <Button asChild variant="outline" className="h-11 flex-1"><Link to={`/app/deals/${d.id}`} data-testid={`deal-details-btn-${d.ref}`}>Details</Link></Button>
        <Button asChild className="h-11 flex-1" disabled={d.availability === "out_of_stock"}>
          <Link to={`/app/book?deal=${d.id}`} data-testid={`deal-book-btn-${d.ref}`}>Book order</Link>
        </Button>
      </div>
    </div>
  </div>
);

export default function FindDeals() {
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [cats, setCats] = useState([]);
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/deals/categories").then((r) => setCats(r.data)); }, []);
  useEffect(() => {
    const t = setTimeout(() => api.get("/deals", { params: { q, category: cat, page } }).then((r) => setData(r.data)), 250);
    return () => clearTimeout(t);
  }, [q, cat, page]);
  return (
    <div>
      <PageHeader title="Find Deals" subtitle="Fixed prices set by CommissionWorld. Share these with your customers and earn on every completed order." />
      <div className="mb-6 space-y-3">
        <div className="relative"><Search className="absolute left-3 top-3.5 h-5 w-5 text-slate-400" />
          <Input data-testid="deals-search-input" placeholder="Search deals by name or reference" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} className="h-12 pl-10" /></div>
        <div className="flex gap-2 overflow-x-auto pb-1">
          {["", ...cats].map((c) => (
            <button key={c || "all"} onClick={() => { setCat(c); setPage(1); }} data-testid={`category-filter-${c || "all"}`}
              className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-medium ${cat === c ? "border-[#0B192C] bg-[#0B192C] text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>{c || "All deals"}</button>
          ))}
        </div>
      </div>
      {!data ? <Loading /> : data.items.length === 0 ? <EmptyState icon={PackageOpen} title="No deals found" message="Try another search or check back soon for new deals." /> : (
        <>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{data.items.map((d) => <DealCard key={d.id} d={d} />)}</div>
          <Pager page={data.page} pages={data.pages} total={data.total} onPage={setPage} />
        </>
      )}
    </div>
  );
}
